import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { formatBookingEffReferenceFromParts } from "../../utils/bookingUtils";

function transportAbbrevFromRow(
  transportTypeInfo: unknown
): string | undefined {
  if (Array.isArray(transportTypeInfo)) {
    return transportTypeInfo[0]?.abbreviation as string | undefined;
  }
  if (transportTypeInfo && typeof transportTypeInfo === "object") {
    return (transportTypeInfo as { abbreviation?: string }).abbreviation;
  }
  return undefined;
}

export async function fetchBookingEffReferences(
  bookingIds: number[]
): Promise<Record<number, string>> {
  const unique = [...new Set(bookingIds.filter((id) => id > 0))];
  if (!unique.length) return {};

  const { data, error } = await supabase
    .from("bookings")
    .select("id, transport_type_info:booking_transport_types(abbreviation)")
    .in("id", unique);

  const map: Record<number, string> = {};
  if (!error) {
    for (const row of data ?? []) {
      const id = row.id as number;
      map[id] = formatBookingEffReferenceFromParts(
        id,
        transportAbbrevFromRow(row.transport_type_info)
      );
    }
  }

  for (const id of unique) {
    if (!map[id]) {
      map[id] = formatBookingEffReferenceFromParts(id);
    }
  }

  return map;
}

export function useBookingEffReferences(
  bookingIds: number[]
): Record<number, string> {
  const [refs, setRefs] = useState<Record<number, string>>({});
  const idsKey = bookingIds.join(",");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await fetchBookingEffReferences(bookingIds);
      if (!cancelled) setRefs(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps -- keyed by id list

  return refs;
}
